/**
 * useFetch helper — wrapper sobre fetch() com:
 * - credentials: 'include' (cookie de sessão)
 * - X-CSRF-Token header automático em mutações
 * - retry 1x em 403 com refresh do CSRF via /me
 *
 * NÃO é hook React — é função pura usada pelos hooks/components.
 * Token CSRF vive no closure do AuthProvider (não em localStorage por RN22).
 */

export interface FetchOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface FetchResult<T> {
  ok: boolean;
  status: number;
  data?: T;
  error?: { code: string; message: string };
}

export type CsrfRefresher = () => Promise<string | null>;

export async function apiFetch<T = unknown>(
  url: string,
  options: FetchOptions = {},
  csrfToken: string | null,
  refreshCsrf: CsrfRefresher
): Promise<FetchResult<T>> {
  const method = options.method ?? 'GET';
  const isMutation = method !== 'GET';

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options.headers ?? {})
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (isMutation && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
    signal: options.signal
  };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);

  let res = await fetch(url, init);

  // Retry 1x em 403 CSRF_INVALID
  if (res.status === 403 && isMutation) {
    const fresh = await refreshCsrf();
    if (fresh) {
      headers['X-CSRF-Token'] = fresh;
      res = await fetch(url, { ...init, headers });
    }
  }

  let data: any = null;
  if (res.status !== 204) {
    try {
      data = await res.json();
    } catch {
      // body vazio ou não-JSON
    }
  }

  if (res.ok) {
    return { ok: true, status: res.status, data: data as T };
  }
  return {
    ok: false,
    status: res.status,
    error: {
      code: data?.code ?? data?.codigo ?? 'INTERNAL',
      message: data?.message ?? data?.msg ?? 'Erro desconhecido'
    }
  };
}
